import { createFileRoute, Link } from '@tanstack/react-router';

import { useArticlesList } from '~/features/articles/hooks.js';
import { useAuth } from '~/features/auth/hooks.js';
import { useDocumentsList } from '~/features/documents/hooks.js';
import { useLeadsList } from '~/features/leads/hooks.js';
import { useProjectsList } from '~/features/projects/hooks.js';

export const Route = createFileRoute('/_auth/dashboard')({
  component: DashboardPage,
});

/**
 * Primeira tela após o login. Mostra números reais do que já está no ar e
 * do que precisa de atenção, cada um levando direto à área correspondente
 * — a página serve tanto de resumo quanto de porta de entrada para quem
 * ainda está aprendendo onde fica cada coisa.
 */
function DashboardPage() {
  const { user } = useAuth();

  const emRevisao = useArticlesList({ status: 'em_revisao', perPage: 1 });
  const publicados = useArticlesList({ status: 'publicado', perPage: 1 });
  const projetos = useProjectsList({ perPage: 1 });
  const mensagens = useLeadsList({ perPage: 1 });
  const documentos = useDocumentsList({});

  return (
    <div className="page">
      <header className="page-head">
        <p className="eyebrow">/ Visão geral</p>
        <h1 className="page-title">Olá, {user?.nome.split(' ')[0] ?? 'bem-vindo'}.</h1>
        <p className="page-sub">
          Um resumo do que está no ar e do que espera por você. Clique em qualquer número para ir
          direto à área.
        </p>
      </header>

      <section className="kpi-grid" aria-label="Resumo do site">
        <KpiCard
          to="/leads"
          label="Mensagens recebidas"
          value={mensagens.data?.total}
          hint="Pelos formulários do site"
        />
        <KpiCard
          to="/articles"
          label="Artigos aguardando revisão"
          value={emRevisao.data?.total}
          hint="Precisam de alguém para aprovar"
        />
        <KpiCard
          to="/articles"
          label="Artigos publicados"
          value={publicados.data?.total}
          hint="No ar quando o blog for ligado"
        />
        <KpiCard
          to="/projects"
          label="Projetos no portfólio"
          value={projetos.data?.total}
          hint="Cases e pinos do mapa"
        />
        <KpiCard
          to="/documents"
          label="Documentos publicados"
          value={documentos.data?.filter((d) => d.visivel).length}
          hint="Disponíveis em Transparência"
        />
      </section>
    </div>
  );
}

function KpiCard({
  to,
  label,
  value,
  hint,
}: {
  to: string;
  label: string;
  value: number | undefined;
  hint: string;
}) {
  return (
    <Link to={to} className="kpi-card kpi-card--link">
      <p className="kpi-label">{label}</p>
      {/* `undefined` = ainda carregando. Zero é um número legítimo e
          precisa aparecer como 0, não como travessão. */}
      <p className="kpi-value">{value === undefined ? '—' : value}</p>
      <p className="kpi-hint">{hint}</p>
    </Link>
  );
}
